import { SplitSheet } from "@/features/splits/split_sheet";
import { useSplitCommands, useSplitMeName } from "@/hooks/use_splits";
import { useOnline } from "@/hooks/use_online";
/**
 * Capture — the front door (SPEC.md §3, DESIGN_SYSTEM §4.8).
 *
 * The app and both platform shortcuts open directly here. Not a dashboard with
 * a button on it: the numpad itself, on the default category (★) — or the last
 * one used — so a repeat purchase is digits → Save.
 *
 * Open Water layout: the top is what you read (the amount, and what it does to
 * the category), the bottom what you touch (the category grid, the numpad,
 * Save). Behind it all, the selected category's water: its level is how much
 * of the budget is spent, and a faint preview line shows where the amount
 * being typed would take it — arithmetic on the number, never a projection
 * (`risePreview`).
 *
 * INV-4 — Save writes to IndexedDB and returns. There is no `await` on the
 * network anywhere in this file, and no spinner. `scripts/check-invariants.sh`
 * fails the build if one appears.
 */

import { useFamilyData } from "@/hooks/use_family_data";
import { mergeGrid } from "@/domain/merged_grid";
import { startCategoryId } from "@/domain/default_category";
import type { Category } from "@/lib/types";
import type { LedgerRef } from "@/lib/ledger";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";

import { type Cents, ZERO, cents, formatMoney, toInputString, tryParseAmount } from "@/domain/money";
import { type RisePreview, risePreview } from "@/domain/rise_preview";
import { GRID_ROWS_SHORT, GRID_ROWS_TALL, gridLayout } from "@/features/capture/grid_layout";
import { MoreSheet } from "@/features/capture/more_sheet";
import { CategoryGlyph } from "@/features/common/category_glyph";
import { cx } from "@/features/common/primitives";
import { UndoToast } from "@/features/common/undo_toast";
import { WATER_FLOOR, waterLevel } from "@/features/horizon/water_level";
import { useCategoriesWithLoad, usePeriodView, useProfile, useStartCategoryId } from "@/hooks/use_data";
import { useHaptics } from "@/hooks/use_haptics";
import { capturePrefill } from "@/native/deep_links";
import { playDropSound } from "@/lib/drop_sound";
import { crossedMarks } from "@/domain/thresholds";
import { useLapse } from "@/hooks/use_lapse";
import { useUndoWindow } from "@/hooks/use_undo_window";
import { addExpense, undoExpense } from "@/lib/repo";
import { WaterSurface } from "@/water/water_surface";

const THRESHOLD_TOAST_MS = 5_000;
const MAX_DIGITS = 9;
const NOTE_MAX_LENGTH = 280;
/** Below this viewport height the grid keeps to two rows, so the numpad and Save still fit (320×568). */
const SHORT_SCREEN_QUERY = "(max-height: 640px)";
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "del"] as const;

interface JustSaved {
  id: string;
  ledger?: LedgerRef;
  amount: Cents;
  categoryName: string;
}

/** Two grid rows on a short screen, three otherwise — and it follows a rotation or resize. */
function useGridRows(): number {
  const [short, setShort] = useState(() => window.matchMedia(SHORT_SCREEN_QUERY).matches);
  useEffect(() => {
    const media = window.matchMedia(SHORT_SCREEN_QUERY);
    const onChange = (): void => setShort(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return short ? GRID_ROWS_SHORT : GRID_ROWS_TALL;
}

export function CaptureView(): React.JSX.Element {
  const navigate = useNavigate();
  const online = useOnline();
  const { captureBlocked, isCaptureBlocked } = useLapse();
  const { rows: personalCategories, loaded: categoriesLoaded } = useCategoriesWithLoad();
  const profile = useProfile();
  const view = usePeriodView();
  const personalStartId = useStartCategoryId();
  const familyData = useFamilyData();
  const familyReady = !!familyData?.family && (!!familyData.profile || !!familyData.familyError);
  const categories = mergeGrid<Category & { ledger: "personal" | "family" }>(
    personalCategories.map(category => ({ ...category, ledger: "personal" })),
    (familyReady ? familyData.categories : []).map(category => ({ ...category, ledger: "family" })),
    familyReady ? profile.gridOrder : null,
  );
  const familyIds = new Set(familyData?.categories.map(c => c.id) ?? []);
  const startId = familyData?.family && familyData.view ? startCategoryId({ defaultId: profile.defaultCategoryId, lastUsedId: familyData.last, live: categories }) : personalStartId;
  const haptic = useHaptics();
  const rows = useGridRows();

  const [thresholdToast, setThresholdToast] = useState<string | null>(null);
  useEffect(() => {
    if (thresholdToast === null) return;
    const timer = setTimeout(() => setThresholdToast(null), THRESHOLD_TOAST_MS);
    return () => clearTimeout(timer);
  }, [thresholdToast]);

  const [digits, setDigits] = useState("");
  // The category chosen on this visit. Null means "open on the start category"
  // — and it goes back to null whenever the app returns to the foreground, so
  // the next purchase starts from the default again rather than from whatever
  // was tapped last week.
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const splitCommands = useSplitCommands();
  const { undoing: splitUndoing, dismiss: dismissSplitUndo } = splitCommands.undo;
  const splitMeName = useSplitMeName();
  const [poppedId, setPoppedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rippleKey, setRippleKey] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();

  // W-6 — a link, an icon shortcut or a widget may open `/?category=…&amount=…`
  // to **prefill** the numpad. It never saves: the user still taps Save, which
  // is what makes it safe for any app on the phone to open such a link
  // (SPEC §15). Applied once, then dropped from the URL, so a Save followed by
  // a reload does not put the same amount back.
  const search = searchParams.toString();
  const categoriesReady = categories.length > 0 && familyData !== undefined;
  useEffect(() => {
    if (search === "" || !categoriesReady) return;
    const prefill = capturePrefill(`?${search}`);
    if (prefill !== null) {
      if (prefill.amountCents !== null) setDigits(toInputString(cents(prefill.amountCents)));
      // An id this ledger does not have is ignored rather than selected into
      // a void; the last-used category stands.
      if (prefill.categoryId !== null && categories.some((c) => c.id === prefill.categoryId)) {
        setChosenId(prefill.categoryId);
      }
    }
    setSearchParams({}, { replace: true });
  }, [search, categoriesReady, categories, setSearchParams]);

  // Undo the last Save — catches most fat-finger errors with zero navigation
  // (SPEC.md §3.3). The toast stays up until the removal has committed; the hook
  // owns why.
  const undoWindow = useUndoWindow<JustSaved>({
    onUndo: ({ id, ledger }) => ledger ? undoExpense(id, ledger) : undoExpense(id),
    onError: (cause) => {
      setError(
        cause instanceof Error
          ? `Couldn't undo that: ${cause.message}`
          : "Couldn't undo that. It is still in your ledger.",
      );
    },
  });
  const openUndo = undoWindow.open;
  const dismissUndo = undoWindow.dismiss;
  const saved = undoWindow.pending;

  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === "visible") setChosenId(null);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const chosenLive = chosenId !== null && categories.some((c) => c.id === chosenId);
  const selectedId = chosenLive ? chosenId : startId;
  const selected = categories.find((c) => c.id === selectedId) ?? null;
  const selectedLedger = selected?.ledger === "family" ? familyData?.ledger : undefined;
  const selectedView = selectedLedger ? familyData?.view ?? view : view;
  const standing = selectedView.standings.find((s) => s.category.id === selectedId) ?? null;

  const amount = tryParseAmount(digits === "" ? "0" : digits);
  const typing = amount !== null && amount > 0;
  const canSave = !captureBlocked && typing && selected !== null;
  const rise = standing ? risePreview(standing, typing ? amount : ZERO) : null;

  const level = rise?.hasBudget ? waterLevel(true, rise.ratioBefore) : WATER_FLOOR;
  const previewLevel = rise?.hasBudget && typing ? waterLevel(true, rise.ratioAfter) : null;
  const crossesAMark = (rise?.crossed.length ?? 0) > 0;

  const press = useCallback(
    (key: string) => {
      if (!undoWindow.undoing) dismissUndo();
      if (!splitUndoing) dismissSplitUndo();
      setError(null);
      haptic("key");
      setDigits((current) => {
        if (key === "del") return current.slice(0, -1);
        if (key === ".") {
          if (current.includes(".") || current === "") return current;
          return `${current}.`;
        }
        const dot = current.indexOf(".");
        if (dot !== -1 && current.length - dot > 2) return current;
        if (current.replace(".", "").length >= MAX_DIGITS) return current;
        if (current === "0" && key !== ".") return key;
        return current + key;
      });
    },
    [haptic, dismissUndo, undoWindow.undoing, splitUndoing, dismissSplitUndo],
  );

  const save = useCallback(() => {
    if (!categoriesLoaded || familyData === undefined || isCaptureBlocked() || amount === null || amount <= 0 || selected === null) return;

    const input = {
      categoryId: selected.id,
      amountCents: amount,
      note: note.trim() === "" ? null : note.trim(),
    };
    (selectedLedger ? addExpense(input, selectedLedger) : addExpense(input))
      .then((expense) => {
        if (standing) {
          const marks = crossedMarks(standing.spent, cents(standing.spent + amount), standing.budget);
          if (marks.length > 0) setThresholdToast(`${selected.name} reached ${String(marks[marks.length - 1])}% of its budget`);
        }
        // A mark crossed is the one thing worth feeling as well as seeing.

        // Stay on what was just used for the next one, until the app is left.
        setChosenId(selected.id);
        setRippleKey((k) => k + 1);
        openUndo({ id: expense.id, ledger: selectedLedger, amount, categoryName: selected.name });
        setDigits("");
        setNote("");
        setShowNote(false);
        try {
          haptic("commit");
          playDropSound();
          if (crossesAMark) haptic("warning");
        } catch (cause) {
          console.warn("Expense saved, but feedback failed:", cause);
          setError("Expense saved. Could not play save feedback.");
        }
      }, (cause: unknown) => {
        // Fail loud (INV-7). A save that silently did nothing is the worst
        // possible outcome for a tracker.
        setError(
          cause instanceof Error
            ? `Couldn't save: ${cause.message}`
            : "Couldn't save that expense. Please try again.",
        );
      }).catch((cause: unknown) => {
        console.error("Expense saved, but its confirmation failed:", cause);
        setDigits("");
        setError("Expense saved. Could not show its confirmation. Check your ledger before logging it again.");
      });
  }, [categoriesLoaded, familyData, isCaptureBlocked, amount, note, selected, selectedLedger, standing, crossesAMark, haptic, openUndo]);

  // W-8 — the numpad is fully operable from a hardware keyboard.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.target instanceof HTMLInputElement) return;
      // The More sheet owns the keyboard while it is open.
      if (moreOpen || document.querySelector('[aria-modal="true"]')) return;
      // A focused button activates itself on Enter. Handling it here as well
      // would run this shortcut AND `preventDefault` the button's own click —
      // so tabbing to any control on this screen and pressing Enter would save
      // an expense instead of doing what the control says. Digits are not
      // exempted: they belong to the numpad wherever focus happens to be.
      if (event.key === "Enter" && event.target instanceof HTMLButtonElement) return;
      if (/^[0-9]$/.test(event.key)) press(event.key);
      else if (event.key === "." || event.key === ",") press(".");
      else if (event.key === "Backspace") press("del");
      else if (event.key === "Enter") save();
      // This screen is the root, so Escape means "clear what I have typed".
      else if (event.key === "Escape") setDigits("");
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [press, save, moreOpen]);

  if (!categoriesLoaded || familyData === undefined) return <div className="capture-surface min-h-dvh bg-bg" />;

  // A brand-new install has no categories until the first sync lands. Showing a
  // numpad with a dead Save button and no explanation is exactly the silent
  // failure C-8 exists to prevent.
  if (categories.length === 0) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-phi-n2 bg-bg px-phi-3 text-center">
        <p className="text-phi-0 font-semibold">Getting your categories</p>
        <p className="max-w-phi-12 text-phi-n1 text-tx-2">
          This happens once, the first time you sign in. If it does not finish,
          check your connection — everything you log afterwards works offline.
        </p>
        <button
          type="button"
          onClick={() => void navigate("/home")}
          className="min-h-[44px] text-phi-n1 font-medium text-acc"
        >
          Go to your waterline
        </button>
      </div>
    );
  }

  const layout = gridLayout(categories, rows, selectedId);
  const currency = selectedLedger ? selectedView.currency : profile.currency;

  return (
    <div className="capture-surface @container relative isolate flex min-h-dvh flex-col bg-bg pt-safe pb-safe-route">
      {/* The selected category's water, behind everything. Decoration (D-4):
          every figure it shows is also written in the line under the amount. */}
      <WaterSurface
        level={level}
        preview={previewLevel}
        isOver={rise?.overBefore ?? false}
        rippleKey={rippleKey}
        className="pointer-events-none absolute inset-0 -z-10"
      />


      <div className="capture-upper">
        {/* ── What you read ── */}
        <div className="capture-header flex items-center justify-between gap-phi-n2 px-phi-1 pt-phi-n4 short:hidden">
          <span className="text-phi-n1 font-semibold tracking-[0.16em] text-tx-2 uppercase">
            Add · {currency}
          </span>
          {/* The way into the agent from the front door. A chip, not a tab: the
              numpad stays the fastest path to logging a coffee. A route change,
              not a network call — INV-4 is untouched by it. */}
          <button
            type="button"
            onClick={() => void navigate("/ask")}
            className="min-h-[44px] min-w-[44px] rounded-full border border-line px-phi-0 text-phi-n1 font-semibold text-tx"
          >
            Ask<span className="sr-only"> the AI Agent</span>
          </button>
        </div>

        <div className="capture-amount flex min-h-0 flex-1 flex-col items-center justify-center gap-phi-n3 px-phi-1 py-phi-n2 text-center short:gap-phi-n4 short:py-0">
          <output
            className={cx(
              "fig text-[length:min(var(--text-phi-6),18cqi,8dvh)] leading-[1.2] font-light tracking-[-0.03em]",
              digits === "" && "text-tx-2",
            )}
          >
            <span className="align-super text-phi-1">$</span>
            {digits === "" ? "0.00" : digits}
          </output>
          <RiseLine name={selected ? `${selected.name}${selectedLedger ? "·👪" : ""}` : ""} rise={rise} typing={typing} currency={currency} family={selectedLedger !== undefined} />
          {familyData?.familyError ? <p role="alert" className="text-phi-n1 text-over">{familyData.familyError}</p> : null}
          {error ? (
            <p role="alert" className="text-phi-n1 text-over">
              {error}
            </p>
          ) : null}
        </div>

        {/* ── What you touch ── */}
        <div role="radiogroup" aria-label="Category" className="capture-categories grid shrink-0 grid-cols-4 gap-x-phi-n4 px-phi-n2">
          {layout.shown.map((category) => {
            const isSelected = category.id === selectedId;
            const isDefault = category.id === profile.defaultCategoryId;
            return (
              <button
                key={category.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => {
                  if (!isSelected) { haptic("select"); setPoppedId(category.id); setChosenId(category.id); }
                }}
                className={cx(
                  "relative flex min-h-[44px] min-w-0 flex-col items-center gap-phi-n8 rounded-phi-n2 px-phi-n8 py-phi-n7",
                  isSelected ? "bg-acc-soft font-bold text-tx" : "text-tx",
                )}
              >
                <span key={`${category.id}-${isSelected}`} className={cx("relative short:hidden", isSelected && poppedId === category.id && "feel-category-pop")}><CategoryGlyph category={category} />{familyIds.has(category.id) ? <span className="absolute -right-phi-n6 -bottom-phi-n6 rounded-full bg-bg text-phi-n4 leading-none" role="img" aria-label="Family">👪</span> : null}</span>
                <span className="line-clamp-2 w-full text-phi-n1 leading-tight text-balance [overflow-wrap:anywhere]">
                  {category.name}{familyIds.has(category.id) ? <span className="hidden short:inline" role="img" aria-label="Family"> · 👪</span> : null}
                </span>
                {isDefault ? (
                  <>
                    <span aria-hidden="true" className="absolute top-0 right-phi-n4 text-phi-n1 leading-none text-acc">
                      ★
                    </span>
                    <span className="sr-only">, your default</span>
                  </>
                ) : null}
              </button>
            );
          })}
          {layout.hidden > 0 ? (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-label={`More categories, ${String(layout.hidden)} more`}
              className="flex min-h-[44px] min-w-0 flex-col items-center gap-phi-n8 rounded-phi-n2 px-phi-n8 py-phi-n7 font-bold text-acc"
            >
              <span
                aria-hidden="true"
                className="inline-flex size-phi-3 short:hidden items-center justify-center rounded-full border border-acc text-phi-0"
              >
                ⋯
              </span>
              <span className="line-clamp-1 w-full text-phi-n1 leading-tight">More</span>
            </button>
          ) : null}
        </div>

        {/* Optional note — last, and never required (SPEC.md §3.1). */}
        <div className="flex shrink-0 items-center justify-between gap-phi-n2 px-phi-1">
          {showNote ? (
            <input
              type="text"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Note (optional)"
              maxLength={NOTE_MAX_LENGTH}
              autoFocus
              className="my-phi-n4 min-h-[44px] min-w-0 flex-1 rounded-phi-n4 border border-line bg-bg-2 px-phi-n1 text-phi-0 text-tx placeholder:text-tx-2"
            />
          ) : (
            <button type="button" onClick={() => setShowNote(true)} className="min-h-[44px] text-phi-n1 text-tx-2">
              + Add a note
            </button>
          )}
          <button type="button" onClick={() => { dismissUndo(); setSplitOpen(true); }} disabled={captureBlocked || personalCategories.length === 0} className="min-h-[44px] shrink-0 text-phi-n1 font-semibold text-acc disabled:opacity-35">Split</button>
          <button type="button" onClick={() => void navigate("/ask")} className="hidden min-h-[44px] min-w-[44px] shrink-0 text-phi-n1 font-semibold text-acc short:block">Ask<span className="sr-only"> the AI Agent</span></button>
        </div>

      </div>

      {/* Numpad — no plates, just the figures (D-1). Keys shrink on a short
          screen so the grid, the numpad and Save fit without a scroll; `--key-h`
          is the one height every key and its figure are drawn from. */}
      <div className="grid shrink-0 grid-cols-3 gap-x-phi-0 px-phi-2 [--key-h:clamp(44px,7dvh,var(--spacing-phi-6))]">
        {KEYS.map((key) => (
          <button
            data-no-press
            key={key}
            type="button"
            onClick={() => press(key)}
            aria-label={key === "del" ? "Delete last digit" : key}
            className={cx(
              "fig flex h-(--key-h) min-h-[44px] items-center justify-center rounded-phi-n2 leading-none font-light",
              // The figure is never more than φ⁻¹ of the key, so a short screen or
              // a large text setting shrinks the digit rather than spilling it.
              "text-[length:min(var(--text-phi-3),calc(var(--key-h)*0.618))]",
              "transition-transform active:scale-[0.94] active:bg-acc-soft",
            )}
          >
            {key === "del" ? (
              <svg viewBox="0 0 24 24" className="size-phi-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6H9l-5 6 5 6h11a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1ZM17 10l-4 4M13 10l4 4" />
              </svg>
            ) : (
              key
            )}
          </button>
        ))}
      </div>

      <div className="shrink-0 px-phi-1 pt-phi-n2 short:pt-phi-n4">
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          className={cx(
            "capture-save flex min-h-phi-5 w-full items-center justify-center rounded-phi-n2 px-phi-0 text-center short:min-h-11",
            "bg-acc text-phi-0 font-bold text-on-acc",
            "disabled:opacity-35",
          )}
        >
          {canSave ? selectedLedger ? `Save ${formatMoney(amount, currency)} → Family` : `Save ${formatMoney(amount, currency)} to ${selected.name}` : "Save expense"}
        </button>
      </div>

      {moreOpen ? (
        <MoreSheet
          categories={categories}
          familyName={familyData?.family?.name}
          familyIds={familyIds}
          selectedId={selectedId}
          onPick={(id) => {
            if (id !== selectedId) { haptic("select"); setPoppedId(id); }
            setChosenId(id);
            setMoreOpen(false);
          }}
          onClose={() => setMoreOpen(false)}
        />
      ) : null}

      {splitOpen ? <SplitSheet initialAmount={amount ?? ZERO} categories={personalCategories} categoryId={selected?.ledger === "personal" ? selected.id : personalCategories[0]?.id ?? ""} description={note.trim() || selected?.name || "Expense"} homeCurrency={profile.currency} timeZone={profile.timezone} meName={splitMeName} commands={splitCommands} onClose={() => setSplitOpen(false)} onSaved={() => { setSplitOpen(false); setDigits(""); setNote(""); setShowNote(false); setRippleKey(value => value + 1); haptic("commit"); }} /> : null}
      {splitCommands.undo.pending ? <UndoToast anchor="action" message={splitCommands.undo.pending.message} busy={splitCommands.undo.undoing} onUndo={splitCommands.undo.undo} onDismiss={splitCommands.undo.dismiss} /> : null}
      {splitCommands.error ? <p role="alert" className="px-phi-1 text-phi-n1 text-over">{splitCommands.error}</p> : null}

      {/* Portalled for the same reason as UndoToast: beside Home, Capture's pane would bury it. */}
      {thresholdToast ? createPortal(<p role="status" className="fixed inset-x-0 bottom-36 bg-bg-2 px-phi-0 py-phi-n2 text-center text-phi-n1">{thresholdToast}</p>, document.body) : null}
      {saved ? (
        <UndoToast
          anchor="action"
          busy={undoWindow.undoing}
          onUndo={undoWindow.undo}
          onDismiss={undoWindow.dismiss}
          message={
            <>
              <span className="fig shrink-0 font-semibold">{formatMoney(saved.amount, currency)}</span>{" "}
              <span className="min-w-0 truncate text-tx-2">{saved.ledger ? `Saved to Family${online ? "" : " · uploads when you’re online"}` : `logged to ${saved.categoryName}`}</span>
            </>
          }
        />
      ) : null}
    </div>
  );
}

/**
 * The line under the amount: the category, and what this amount does to it.
 *
 * "Food & Drink · $87.60 → $83.10 left · rises to 86%". Over the mark the
 * figures turn coral **and** say "over" (D-8 — never colour alone). Without a
 * budget it says what has been spent, and nothing it cannot know.
 */
function RiseLine({
  name,
  rise,
  typing,
  currency,
  family = false,
}: {
  name: string;
  rise: RisePreview | null;
  typing: boolean;
  currency: string;
  family?: boolean;
}): React.JSX.Element {
  const money = (value: Cents): string => formatMoney(value, currency);
  const left = (value: Cents): string => (value < 0 ? `${money(cents(0 - value))} over` : `${money(value)} left`);

  let detail: React.ReactNode = "Type an amount";
  if (rise && !rise.hasBudget) {
    detail = <span className="fig">{money(rise.spentAfter)} spent{typing ? " with this" : " so far"}</span>;
  } else if (rise && rise.leftBefore !== null && rise.leftAfter !== null) {
    const before = (
      <span className={cx("fig", rise.overBefore && "font-semibold text-over")}>{left(rise.leftBefore)}</span>
    );
    detail = typing ? (
      <>
        <span className={cx("fig", rise.overBefore && "font-semibold text-over")}>
          {rise.overBefore ? left(rise.leftBefore) : money(rise.leftBefore)}
        </span>
        {" → "}
        <b className={cx("fig", rise.overAfter ? "text-over" : "text-tx")}>{left(rise.leftAfter)}</b>
        {rise.overAfter ? null : <> · rises to {String(rise.percentAfter)}%</>}
      </>
    ) : (
      <>
        {before} · {String(rise.percentBefore)}% used
      </>
    );
  }

  return (
    <p className="max-w-full text-phi-n1 text-balance text-tx-2">
      {name === "" ? null : (
        <>
          <b className="text-tx">{name}</b>{family ? " " : " · "}
        </>
      )}
      {detail}
    </p>
  );
}
